"use client";
"use no memo";

import { ChatChannelsQuery } from "@/hooks/queries/chat/use-channels-query";

import { Routes } from "@/api";
import { Channel, DEFAULT_CHANNEL_PREFIX } from "@/controllers/channel/schema";
import { useIsDesktop } from "@/hooks/layout/use-is-desktop";
import { UpdateMessageStatusMutation } from "@/hooks/mutations/chat/messages/use-update-message-status-mutation";
import { ChatMessagesLatestMessageQuery } from "@/hooks/queries/chat/messages/use-chat-messages-latest-message-query";
import { ChatSocketQuery } from "@/hooks/queries/chat/use-chat-socket-query";
import { HasUnreadNotificationsQuery } from "@/hooks/queries/user/use-has-unread-notifications-query";
import { useToggle } from "@/hooks/use-toggle";
import { useVirtualizedContent } from "@/hooks/use-virtualized-content";
import { cn } from "@/lib/utils";
import { useInboxSidebarStore } from "@/stores/sidebar-store";
import { capitalize } from "@/utils/capitalize";
import { getMemberLabels } from "@/utils/chat/get-member-labels";
import { ChannelType } from "@prisma/client";
import { formatDate } from "date-fns";
import {
  CircleDollarSignIcon,
  Loader2Icon,
  LockIcon,
  UserIcon,
  UserPlus2Icon,
  UsersIcon,
} from "lucide-react";
import { useSession } from "next-auth/react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import React, { PropsWithChildren } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { ScrollArea } from "../ui/scroll-area";
import { SheetClose } from "../ui/sheet";
import { useSidebar } from "../ui/sidebar";
import { UserAvatar } from "../user/user-avatar";

const CHANNEL_HEIGHT = 72;

function estimateSize() {
  return CHANNEL_HEIGHT;
}

function orderByRecentMessage(a: Channel, b: Channel) {
  const aMessage = a.messages[0];
  const bMessage = b.messages[0];

  if (!aMessage) {
    return 1;
  }

  if (!bMessage) {
    return -1;
  }

  return bMessage.createdAt.getTime() - aMessage.createdAt.getTime();
}

interface ChatListProps {
  readonly closeSheetOnClick?: boolean;
}

export function ChatList(props: ChatListProps) {
  const pathname = usePathname();
  const channelsQuery = ChatChannelsQuery.useInfiniteQuery();
  const channels = React.useMemo(() => {
    return (channelsQuery.data?.pages.flat() ?? []).sort(orderByRecentMessage);
  }, [channelsQuery.data?.pages]);

  const { virtualizer, parentRef } = useVirtualizedContent({
    ...channelsQuery,
    hasPages: Boolean(channelsQuery.data?.pages),
    data: channels,
    estimateSize,
  });
  const items = virtualizer.getVirtualItems();

  return (
    <ScrollArea
      className="h-[calc(100%-3rem)] min-h-[100px] w-full"
      ref={parentRef}
    >
      <ul
        className="relative w-full"
        style={{ height: `${virtualizer.getTotalSize()}px` }}
      >
        {items.map((virtualRow) => {
          const index = virtualRow.index;
          const channel = channels[index];
          const isLoaderRow = virtualRow.index > channels.length - 1;
          const isActiveChannel = Boolean(
            channel && pathname?.includes(channel.id),
          );

          return (
            <div
              key={`${virtualRow.key}-${channel?.id ?? "loader-row"}`}
              data-index={virtualRow.index}
              ref={virtualizer.measureElement}
              className="absolute left-0 top-0 w-full"
              style={{
                height: `${virtualRow.size}px`,
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              {isLoaderRow ? (
                channelsQuery.hasNextPage ? (
                  <div className="grid w-full place-items-center p-2">
                    <Loader2Icon className="h-8 w-8 animate-spin" />
                  </div>
                ) : (
                  <p className="text-foreground">Nothing more to load</p>
                )
              ) : (
                <ChatItem
                  channel={channel}
                  isActiveChannel={isActiveChannel}
                  closeSheetOnClick={props.closeSheetOnClick}
                />
              )}
            </div>
          );
        })}
      </ul>
    </ScrollArea>
  );
}

interface ChatItemProps {
  readonly channel: Channel;
  readonly isActiveChannel: boolean;
  readonly closeSheetOnClick?: boolean;
}

function ChatItem(props: ChatItemProps) {
  const isDesktop = useIsDesktop();
  const { data: session } = useSession();
  const { setOpenMobile } = useSidebar();
  const lastMessage = props.channel.messages[0];
  const latestMessageQuery = ChatMessagesLatestMessageQuery.useQuery(
    props.channel.id,
  );
  const updateMessageStatusMutation = UpdateMessageStatusMutation.useMutation();
  const hasUnreadNotificationsMutation =
    HasUnreadNotificationsQuery.useMutation();
  const isUnread =
    lastMessage &&
    !lastMessage.readBy.find((r) => r.userId === session?.user?.id)?.readAt;

  const Wrapper = ({ children }: PropsWithChildren) =>
    props.closeSheetOnClick ? (
      <SheetClose asChild>{children}</SheetClose>
    ) : (
      children
    );

  const socketKeys = React.useMemo(() => {
    return ChatSocketQuery.keys.all(props.channel.id);
  }, [props.channel.id]);
  ChatSocketQuery.useQuery(socketKeys);

  const labels = React.useMemo(() => {
    if (!session?.user) {
      return null;
    }

    return getMemberLabels(session?.user, props.channel, true);
  }, [props.channel, session?.user]);

  if (!labels) {
    return null;
  }

  const { memberNames, members } = labels;
  const channelName =
    members.length > 2 || !props.channel.name.startsWith(DEFAULT_CHANNEL_PREFIX)
      ? props.channel.name
      : memberNames;

  const markMessageAsRead = () => {
    if (!isDesktop) {
      useInboxSidebarStore.getState().update(false);
    }

    if (!session?.user.id) {
      throw new Error("User not found");
    }

    // if(!isUnread) {
    //   return;
    // }

    if (lastMessage?.id) {
      updateMessageStatusMutation.mutate({
        userId: session?.user.id,
        channelId: props.channel.id,
        messageIds: [lastMessage.id],
        isRead: true,
      });
    }
    hasUnreadNotificationsMutation.mutate({ types: ["message"] });
  };

  const closeSidebarOnMobile = () => {
    if (isDesktop) {
      return;
    }
    setOpenMobile(false);
  };

  return (
    <li
      className={cn(
        "relative rounded-r-lg transition-colors duration-300 ease-in-out hover:bg-primary-foreground",
        props.isActiveChannel ? "bg-primary-foreground" : "",
      )}
      onClick={closeSidebarOnMobile}
    >
      <div
        className={cn(
          "absolute left-0 top-0 h-full bg-primary transition-all duration-200 ease-in-out",
          props.isActiveChannel ? "w-1 scale-100 rounded-r-lg" : "w-0 scale-0",
          props.channel.type === ChannelType.SUBSCRIBERS ? "bg-warning" : "",
        )}
      />

      <Wrapper>
        <Link
          className="flex w-full items-center justify-between space-x-4 p-4 text-sm"
          href={`${Routes.INBOX_CHAT(props.channel.id)}`}
          onClick={markMessageAsRead}
        >
          <div className="flex w-full items-center justify-center gap-2 md:justify-start">
            <div className="flex items-center justify-center -space-x-5">
              {members.map((member, index) => {
                return (
                  <UserAvatar
                    key={member.id}
                    user={member}
                    badge={index === members.length - 1 ? isUnread : false}
                  />
                );
              })}
            </div>
            <div className="grow flex-col items-center md:flex md:items-start">
              <div className="relative w-full grow">
                <p
                  className={cn(
                    "max-w-[70%] overflow-hidden text-ellipsis whitespace-nowrap",
                    isUnread ? "font-extrabold" : "font-medium",
                  )}
                >
                  {channelName}
                </p>
                <div className="absolute right-0 top-0">
                  <ChannelTypeIcon
                    type={props.channel.type}
                    isUnread={isUnread}
                  />
                </div>
              </div>
              {latestMessageQuery.data ? (
                <LatestMessagePreview
                  message={latestMessageQuery.data}
                  isUnread={isUnread}
                />
              ) : (
                <LatestMessagePreview
                  message={lastMessage}
                  isUnread={isUnread}
                />
              )}
            </div>
          </div>
        </Link>
      </Wrapper>
    </li>
  );
}

function getChannelIcon(type: ChannelType, isUnread: boolean) {
  const strokeWidth = isUnread ? 3 : undefined;

  switch (type) {
    case ChannelType.DIRECT:
      return (
        <UserIcon
          className="size-4 text-muted-foreground"
          strokeWidth={strokeWidth}
        />
      );

    case ChannelType.GROUP:
      return (
        <UsersIcon
          className="size-4 text-muted-foreground"
          strokeWidth={strokeWidth}
        />
      );

    case ChannelType.PRIVATE:
      return (
        <LockIcon
          className="size-4 text-muted-foreground"
          strokeWidth={strokeWidth}
        />
      );

    case ChannelType.FOLLOWERS:
      return (
        <UserPlus2Icon
          className="size-4 text-muted-foreground"
          strokeWidth={strokeWidth}
        />
      );

    case ChannelType.SUBSCRIBERS:
      return (
        <CircleDollarSignIcon
          className={"size-4 text-warning"}
          strokeWidth={strokeWidth}
        />
      );
  }
}

const ChannelTypeIcon = React.memo(function ChannelTypeIcon(
  props: Pick<Channel, "type"> & { isUnread: boolean },
) {
  const openToggle = useToggle();
  let Icon = getChannelIcon(props.type, props.isUnread);
  if (!Icon) {
    return null;
  }

  const onIconClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    openToggle.toggle();
  };

  return (
    <Popover open={openToggle.value} onOpenChange={openToggle.toggle}>
      <PopoverTrigger asChild onClick={onIconClick}>
        {Icon}
      </PopoverTrigger>
      <PopoverContent className="w-auto px-2 py-1">
        {capitalize(props.type, true)} chat
      </PopoverContent>
    </Popover>
  );
});

function LatestMessagePreview(props: {
  message: ChatItemProps["channel"]["messages"][number];
  isUnread: boolean;
}) {
  const lastMessage = props.message;

  return (
    <div
      className={cn(
        "flex w-full justify-between",
        props.isUnread ? "font-extrabold" : "",
      )}
    >
      {lastMessage?.content ? (
        <p
          className={cn(
            "truncate text-xs",
            props.isUnread ? "text-foreground" : "text-muted-foreground",
          )}
        >
          {lastMessage?.content.slice(0, 17)}
          {lastMessage?.content.length > 17 ? "..." : ""}
        </p>
      ) : (
        <p className="text-xs italic text-muted-foreground">No messages yet</p>
      )}

      {lastMessage?.createdAt ? (
        <p
          className={cn(
            "text-xs",
            props.isUnread
              ? "font-extrabold text-foreground"
              : "text-muted-foreground",
          )}
        >
          {formatDate(lastMessage?.createdAt, "HH:mm")}
        </p>
      ) : null}
    </div>
  );
}
